import { motion } from "framer-motion";
import { Crown, MapPin, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * BusinessOfTheMonthShowcase
 * Premium "Best of Best" feature slot on the directory home page.
 * Renders the LocalBest TV frame with the featured business's intro video playing inside
 * the screen. Title/location info sits BELOW the TV next to the CTA.
 *
 * To swap next month: edit the FEATURED constant below.
 */
const FEATURED = {
  name: "Chicagoland Group",
  category: "Roofing",
  city: "Naperville",
  state: "IL",
  videoUrl:
    "https://assets.cdn.filesafe.space/hTiHGRdedeEkRW5TnYO1/media/69e8e901717d5dd4e10edf80.mp4",
  logoUrl:
    "https://customer-assets.emergentagent.com/wingman/e059935c-16cc-4334-918d-a09bf9581eeb/attachments/eafb4a63cf6b46919cd1d4e7a944ee5b_image.png",
  listingPath: "/directory/roofing/naperville-il/chicagoland-group",
};

const TV_FRAME_URL =
  "https://customer-assets.emergentagent.com/job_b515077c-a195-4ba1-b1ea-7a33c6e19b28/artifacts/dtloe834_ChatGPT%20Image%20Jun%205%2C%202026%2C%2005_03_10%20PM.png";

export default function BusinessOfTheMonthShowcase() {
  return (
    <section
      className="relative w-full overflow-hidden pt-4 md:pt-6"
      data-testid="business-of-the-month-section"
    >
      {/* TV frame with autoplay video — mobile scales the image so the TV screen fills the viewport width */}
      <div className="relative w-full overflow-hidden" data-testid="botm-tv-wrapper">
        <div className="relative md:w-full md:mx-0 w-[171%] mx-[-35.5%]">
          <img
            src={TV_FRAME_URL}
            alt={`${FEATURED.name} cinematic spotlight`}
            className="block w-full h-auto select-none pointer-events-none"
            draggable="false"
          />
          <div
            className="absolute overflow-hidden bg-black"
            style={{ top: "15%", left: "21%", width: "58.5%", height: "57.5%" }}
            data-testid="botm-video-frame"
          >
            <video
              src={FEATURED.videoUrl}
              autoPlay
              loop
              muted
              playsInline
              controls
              className="w-full h-full object-cover"
              data-testid="botm-video"
            />
          </div>
        </div>
      </div>

      {/* Info + CTA — side by side under the TV */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 md:pt-6 pb-10 md:pb-14">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          {/* Left: badge + name + location */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex-1 text-center md:text-left"
          >
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-[#F59E0B]/15 to-[#EC4899]/15 border border-[#F59E0B]/30 mb-3"
              data-testid="botm-badge"
            >
              <Crown className="w-4 h-4 text-[#F59E0B]" />
              <span className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-[#F59E0B]">
                Best of Best — Business of the Month
              </span>
            </div>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold font-['Outfit'] text-white tracking-tight"
              data-testid="botm-name"
            >
              {FEATURED.name}
            </h2>
            <p className="text-[#94A3B8] text-sm sm:text-base mt-2 flex items-center justify-center md:justify-start gap-1.5">
              <MapPin className="w-4 h-4 text-[#00E5FF]" />
              {FEATURED.category} • {FEATURED.city}, {FEATURED.state}
            </p>
          </motion.div>

          {/* Right: logo + CTA */}
          <div className="flex items-center justify-center md:justify-end gap-4 flex-shrink-0">
            {FEATURED.logoUrl && (
              <img
                src={FEATURED.logoUrl}
                alt={`${FEATURED.name} logo`}
                className="w-16 h-16 rounded-xl object-contain bg-white/5 border border-white/10 p-1"
                data-testid="botm-logo"
              />
            )}
            <Link
              to={FEATURED.listingPath}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-white bg-gradient-to-r from-[#F59E0B] to-[#EC4899] hover:from-[#EC4899] hover:to-[#F59E0B] shadow-lg shadow-[#F59E0B]/20 transition-all"
              data-testid="botm-cta-visit-listing"
            >
              Visit {FEATURED.name}'s Listing
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
